import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { Style, StatusBar } from '@capacitor/status-bar';
import { estNatif, plateformeNative } from './pont';

/**
 * Ce que la coque doit faire au démarrage, et rien de plus.
 *
 * Tout ici est du ressort du système, jamais du produit : la barre d'état, l'écran
 * d'amorçage, le bouton retour d'Android. L'application, elle, ne sait pas qu'elle tourne
 * dans une coque — et c'est la raison pour laquelle il n'y a plus deux interfaces à tenir.
 */

/**
 * Retire l'écran d'amorçage.
 *
 * `launchAutoHide: false` dans la configuration : c'est le code qui décide, pas une durée.
 * Une durée fixe fait toujours l'un des deux — elle coupe trop tôt sur un téléphone lent,
 * laissant voir un fond vide, ou fait attendre pour rien sur un téléphone rapide.
 *
 * Appelé après le premier rendu de React : à ce moment, il y a quelque chose derrière.
 */
export async function retirerAmorcage(): Promise<void> {
  if (!estNatif()) return;
  try {
    await SplashScreen.hide();
  } catch {
    /* un écran d'amorçage qui ne se retire pas se retire seul au bout de son délai */
  }
}

/**
 * Accorde la barre d'état au bandeau de l'application.
 *
 * Le bandeau est marine et son texte blanc ; une barre d'état claire posée dessus rendrait
 * l'heure et la batterie illisibles. `Style.Dark` désigne un **fond** sombre, donc des
 * icônes claires — la nomenclature d'Apple, contre-intuitive et source d'inversions.
 */
export async function accorderBarreEtat(): Promise<void> {
  if (!estNatif()) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    // La couleur de fond n'existe que sur Android : sur iOS, la barre emprunte celle de la
    // vue qui se trouve dessous, et l'appel échouerait sans rien changer.
    if (plateformeNative() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#20418F' });
    }
  } catch {
    /* une barre d'état mal teintée ne vaut pas un écran d'erreur */
  }
}

/**
 * Rend au bouton retour d'Android le sens qu'il a partout ailleurs.
 *
 * Sans cet écouteur, Capacitor ferme l'application au premier appui : un parent qui vient
 * d'ouvrir la messagerie et appuie sur « retour » se retrouve sur son écran d'accueil,
 * application quittée. Ici, il remonte l'historique tant qu'il y a quelque chose derrière,
 * et ne quitte qu'à la racine — le comportement d'une application Android ordinaire.
 *
 * Rend la fonction de retrait : posé deux fois, l'écouteur remonterait deux crans par
 * appui.
 */
export async function brancherRetourArriere(): Promise<() => Promise<void>> {
  if (!estNatif()) return () => Promise.resolve();

  const ecouteur = await App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      globalThis.history.back();
      return;
    }
    void App.exitApp();
  });

  return () => ecouteur.remove();
}

/**
 * Prévient au retour de l'application au premier plan.
 *
 * Ce que cela répare : une application mobile passe l'essentiel de son temps en arrière-
 * plan, et pendant ce temps le dossier vit — l'autre parent déclare une dépense, écrit un
 * message, signale un retard. À la reprise, rien ne redemandait ces nouveautés : les
 * requêtes déjà montées gardaient leur réponse, et il fallait naviguer d'un écran à
 * l'autre pour les faire repartir. C'est le remontage du composant qui rafraîchissait,
 * pas le retour dans l'application.
 *
 * L'événement du système et non `visibilitychange` : dans une WebView, la page ne perd
 * pas toujours sa visibilité quand l'application passe derrière, et le téléphone qui se
 * verrouille ne la lui rend pas toujours. `appStateChange` est le seul signal qui dise
 * vraiment « l'utilisateur est revenu ».
 *
 * Ne se déclenche qu'au retour, jamais au départ : `isActive` porte les deux sens, et
 * rafraîchir en partant ne servirait personne.
 *
 * Rend la fonction de retrait, comme les autres raccords : posé deux fois, chaque reprise
 * relancerait deux fois toutes les requêtes de l'écran.
 */
export async function brancherReprise(auRetour: () => void): Promise<() => Promise<void>> {
  if (!estNatif()) return () => Promise.resolve();

  const ecouteur = await App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) auRetour();
  });

  return () => ecouteur.remove();
}
